import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { HandHeart, Radio } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useI18n } from '../i18n/I18nProvider';
import { fetchPrayers, prayFor, submitPrayer } from '../lib/api';

const POLL_INTERVAL = 5000;

export default function Prayer() {
  const { t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', tag: 'General', text: '', share: true });
  const [prayers, setPrayers] = useState([]);
  const [newIds, setNewIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const lastSeen = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchPrayers();
      setPrayers(data);
      if (data.length) lastSeen.current = data[0].created_at;
    } catch (err) { console.error('Failed to load prayers', err); }
  }, []);

  // Initial load
  useEffect(() => { load(); }, [load]);

  // Poll for new prayers
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        if (!lastSeen.current) {
          await load();
          return;
        }
        const fresh = await fetchPrayers(lastSeen.current);
        if (fresh.length) {
          lastSeen.current = fresh[0].created_at;
          setPrayers((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            const incoming = fresh.filter((p) => !ids.has(p.id));
            if (!incoming.length) return prev;
            setNewIds((prevIds) => {
              const next = new Set(prevIds);
              incoming.forEach((p) => next.add(p.id));
              return next;
            });
            // Remove the 'New' badge after 6s
            incoming.forEach((p) => {
              setTimeout(() => {
                setNewIds((prevIds) => {
                  const next = new Set(prevIds);
                  next.delete(p.id);
                  return next;
                });
              }, 6000);
            });
            return [...incoming, ...prev];
          });
        }
      } catch (err) { console.error('Failed to refresh prayers', err); }
    }, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) {
      toast({ title: t.prayer.missing, description: t.prayer.missingText });
      return;
    }
    setLoading(true);
    try {
      const res = await submitPrayer(form);
      if (form.share) {
        toast({ title: t.prayer.okShared, description: t.prayer.okSharedText });
        if (res?.prayer) {
          lastSeen.current = res.prayer.created_at;
          setPrayers((prev) => [res.prayer, ...prev.filter((p) => p.id !== res.prayer.id)]);
          setNewIds((prevIds) => new Set([...prevIds, res.prayer.id]));
          setTimeout(() => {
            setNewIds((prevIds) => {
              const next = new Set(prevIds);
              next.delete(res.prayer.id);
              return next;
            });
          }, 6000);
        }
      } else {
        toast({ title: t.prayer.okPrivate, description: t.prayer.okPrivateText });
      }
      setForm({ name: '', tag: 'General', text: '', share: true });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const addPray = async (id) => {
    setPrayers((prev) => prev.map((p) => p.id === id ? { ...p, prays: (p.prays || 0) + 1 } : p));
    try { await prayFor(id); } catch (err) { console.error('Failed to record prayer', err); }
  };

  return (
    <section id="prayer" className="relative py-24 lg:py-32 bg-[#efe6cf]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="text-[#b88a2a] text-xs uppercase tracking-wider-xtra font-medium">{t.prayer.eyebrow}</span>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1f3a2a] mt-4 leading-[1.05]">{t.prayer.title_1} <span className="italic">{t.prayer.title_2}</span></h2>
          <p className="mt-6 text-[#4a5a4e] text-lg leading-relaxed">{t.prayer.sub}</p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          <form onSubmit={submit} className="bg-white border border-[#e5dcc5] rounded-3xl p-7 lg:p-9 space-y-5 h-fit">
            <h3 className="font-serif-display text-2xl text-[#1f3a2a]">{t.prayer.formTitle}</h3>
            <p className="text-sm text-[#4a5a4e] -mt-2">{t.prayer.formSub}</p>
            <div>
              <Label className="text-[#1f3a2a]">{t.prayer.yourName} <span className="text-[#8a7a4e]">{t.prayer.optional}</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.prayer.anonymous} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus-visible:ring-[#2e5340]" />
            </div>
            <div>
              <Label className="text-[#1f3a2a]">{t.prayer.connect}</Label>
              <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                <SelectTrigger className="mt-2 bg-[#faf6ea] border-[#e5dcc5] h-12 rounded-xl focus:ring-[#2e5340]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="General">{t.tags.General}</SelectItem>
                  <SelectItem value="Discover">{t.tags.Discover}</SelectItem>
                  <SelectItem value="Grow">{t.tags.Grow}</SelectItem>
                  <SelectItem value="Serve">{t.tags.Serve}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#1f3a2a]">{t.prayer.request} <span className="text-[#b88a2a]">*</span></Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder={t.prayer.requestPh} className="mt-2 bg-[#faf6ea] border-[#e5dcc5] rounded-xl focus-visible:ring-[#2e5340] min-h-[120px]" />
            </div>
            <label className="flex items-start gap-3 text-sm text-[#4a5a4e] cursor-pointer">
              <Checkbox checked={form.share} onCheckedChange={(v) => setForm({ ...form, share: !!v })} className="mt-0.5 border-[#1f3a2a]/40 data-[state=checked]:bg-[#2e5340] data-[state=checked]:border-[#2e5340]" />
              <span>{t.prayer.shareLabel} <span className="text-[#8a7a4e]">{t.prayer.shareHint}</span></span>
            </label>
            <Button type="submit" disabled={loading} className="w-full bg-[#d4a63d] hover:bg-[#b88a2a] text-[#1f3a2a] rounded-full h-12 font-medium">{loading ? '…' : t.prayer.submit}</Button>
          </form>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif-display text-2xl text-[#1f3a2a]">{t.prayer.wallTitle}</h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider-xtra text-[#2e5340] bg-[#e5f0e3] border border-[#c6dbc3] rounded-full px-2.5 py-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2e5340] opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2e5340]"></span>
                </span>
                <Radio size={11} /> {t.prayer.live}
              </span>
            </div>
            <p className="text-sm text-[#4a5a4e] mb-5">{t.prayer.wallSub}</p>
            <div className="space-y-4">
              {prayers.map((p) => (
                <div
                  key={p.id}
                  className={`bg-white border rounded-2xl p-6 transition-all duration-500 ${newIds.has(p.id) ? 'border-[#d4a63d] shadow-[0_15px_40px_-15px_rgba(212,166,61,0.5)] ring-2 ring-[#d4a63d]/30' : 'border-[#e5dcc5]'}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1f3a2a]">{p.name}</span>
                      <span className="text-[10px] uppercase tracking-wider-xtra px-2 py-1 rounded-full bg-[#f0e9d8] text-[#b88a2a]">{t.tags[p.tag] || p.tag}</span>
                      {newIds.has(p.id) && (
                        <span className="text-[10px] uppercase tracking-wider-xtra px-2 py-1 rounded-full bg-[#d4a63d] text-[#1f3a2a] font-medium">{t.prayer.newBadge}</span>
                      )}
                    </div>
                    <span className="text-xs text-[#8a7a4e]">{p.date}</span>
                  </div>
                  <p className="mt-3 text-[#4a5a4e] text-sm leading-relaxed">{p.text}</p>
                  <button onClick={() => addPray(p.id)} className="mt-4 inline-flex items-center gap-2 text-sm text-[#2e5340] hover:text-[#1f3a2a] group">
                    <HandHeart size={16} className="group-hover:scale-110 transition-transform" /> {t.prayer.praying}
                    {p.prays > 0 && <span className="text-xs text-[#8a7a4e]">· {p.prays}</span>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
